/**
 * Pure builder for the `report.sanity_resolution` audit event payload.
 *
 * Lives in its own module because the only call site is a component
 * (SiteRagReportView) — extracting the payload shape lets us pin the
 * audit-trail contract in a test without mounting the whole report
 * view. The actual emit happens via `emitAuditEvent` (soft-fail) at
 * the call site; this helper is concerned only with shaping the
 * before/after/metadata so a future reader of the audit log can
 * reconstruct what the user did.
 */
import type { ApplyDirective, SanityFinding } from '../types/siteRag';
import type { EmitAuditEventInput } from './auditLogClient';

export interface SanityResolutionAuditInput {
  reportId: string;
  reportLabel?: string;
  finding: SanityFinding;
  /** New resolution — `'apply' | 'ignore'`; undefined means the user hit Undo. */
  nextResolution: 'apply' | 'ignore' | undefined;
  /** New userNote — the edited suggestion text when the user edited before applying. */
  nextUserNote?: string;
  /** New applyDirective — present only when nextResolution === 'apply'. */
  nextApplyDirective?: ApplyDirective;
}

export function buildSanityResolutionAuditEvent(
  input: SanityResolutionAuditInput,
): EmitAuditEventInput {
  const { reportId, reportLabel, finding, nextResolution, nextUserNote, nextApplyDirective } = input;
  return {
    action: 'report.sanity_resolution',
    target: { type: 'report', id: reportId, label: reportLabel },
    before: {
      resolution: finding.resolution ?? null,
      userNote: finding.userNote ?? null,
      applyDirective: finding.applyDirective ?? null,
    },
    after: {
      resolution: nextResolution ?? null,
      userNote: nextUserNote ?? null,
      applyDirective: nextResolution === 'apply' ? (nextApplyDirective ?? null) : null,
    },
    metadata: {
      findingId: finding.id,
      findingCategory: finding.category,
      findingSection: finding.section,
      findingSeverity: finding.severity,
    },
  };
}
